/**
 * Copyright (c) 2025 Elara AI Pty Ltd
 * Dual-licensed under AGPL-3.0 and commercial license. See LICENSE for details.
 */

/**
 * Derive the Experiment surface's **presentation** from the numeric contract.
 *
 * The design principle is *visual-first, derived — never authored*: the bound
 * `experiment` function returns one honest {@link "@elaraai/e3-ui"}
 * `Experiment.Types.Result` — the naive vs adjusted effect, confounder balance,
 * propensity overlap, a robustness summary and an **honesty verdict** — and
 * **every** word, colour, bar width and status on the surface is computed here
 * from those numbers plus the column names the user picked. Because the user
 * frames an arbitrary experiment, no sentence can be stored — the confounder
 * reasons, the trade-off line and the refuter descriptions are *templated*.
 *
 * The headline switches on the `verdict`: `causal` / `modest` /
 * `adjustment_insufficient` keep the numeric Answer zone (the engine produced an
 * `adjusted` estimate); `non_identifiable_positivity` and `not_estimable` are
 * **refusals** (`adjusted = none`) that render an explanatory zone instead of a
 * number. The output of `deriveView` is the plain view-model the React shell
 * paints, so the shell JSX stays a dumb painter — all judgement lives here.
 *
 * @packageDocumentation
 */

import { some, none } from '@elaraai/east';
import type { ValueTypeOf, option } from '@elaraai/east';
import { Experiment } from '@elaraai/e3-ui/internal';
import { getSomeorUndefined } from '@elaraai/east-ui-components';
import type { HelpId } from './help.js';

// ---------------------------------------------------------------------------
// Decoded contract shapes — the JS values the bound `experiment` function
// returns / the staged config. Mirror the East types in
// e3-ui/src/experiment/types.ts (snake_case, single consolidated result).
// ---------------------------------------------------------------------------
// Boundary marshalling for decoded East vectors → plain `number[]` (via `arr`/`arrI`
// below). VectorType decodes to a typed array; ArrayType to a plain array — both
// satisfy these structural widths.
type Vec = ArrayLike<number> & Iterable<number>;
type VecI = ArrayLike<bigint> & Iterable<bigint>;

// Every decoded shape is DERIVED from the single source of truth (`Experiment.Types.*`)
// via `ValueTypeOf` — never hand-rolled, so it cannot drift from the East types.
/** The complete, honest experiment result. */
export type ResultValue = ValueTypeOf<typeof Experiment.Types.Result>;
/** The staged experiment config. */
export type ConfigValue = ValueTypeOf<typeof Experiment.Types.Config>;
/** A named configuration — a vetted question + scope + optional precomputed answer. */
export type ConfigurationValue = ValueTypeOf<typeof Experiment.Types.Configuration>;
/** A confidence interval. */
export type Ci = ValueTypeOf<typeof Experiment.Types.Ci>;
/** The adjusted (like-for-like) effect + CI. */
export type AdjustedValue = ValueTypeOf<typeof Experiment.Types.Adjusted>;
/** One confounder's before-adjustment imbalance. */
export type BalanceValue = ValueTypeOf<typeof Experiment.Types.Balance>;
/** The propensity-overlap diagnostic. */
export type OverlapValue = ValueTypeOf<typeof Experiment.Types.Overlap>;
/** The robustness summary. */
export type RefutationValue = ValueTypeOf<typeof Experiment.Types.Refutation>;
/** The unobserved-confounder sensitivity (tipping) curve. */
export type SensitivityValue = ValueTypeOf<typeof Experiment.Types.Sensitivity>;
/** The ALE dose-response curve. */
export type DoseValue = ValueTypeOf<typeof Experiment.Types.DoseResponse>;
/** The honesty verdict — only `not_estimable` carries a reason. */
export type VerdictValue = ValueTypeOf<typeof Experiment.Types.Verdict>;
export type VerdictTag = VerdictValue['type'];
/** A committed journal row. */
export type JournalRowValue = ValueTypeOf<typeof Experiment.Types.Journal>[number];
/** Optional per-column display metadata, keyed by column name. */
export type ColMeta = ValueTypeOf<typeof Experiment.Types.ColumnMeta>;

/** A column the surface can frame over — name + coarse type family. */
export interface Column { name: string; kind: 'boolean' | 'integer' | 'float' | 'string' | 'datetime' | 'other' }

// ---------------------------------------------------------------------------
// View-model — the exact shape the shell paints (kept stable so the JSX is a
// dumb renderer; all judgement is here).
// ---------------------------------------------------------------------------
export interface VMVerdict { tag: VerdictTag; tone: string; label: string }
export interface VMConfounder { col: string; reason: string; imbalance: number; level: string; tone: string }
export interface VMSpec {
    treatment: string; treatmentLabel: string; treatmentKind: string;
    outcome: string; outcomeLabel: string; outcomeKind: string;
    comparison: string; confounders: VMConfounder[]; suggestion: string;
    method: string; target: string; dataLabel: string;
}
export interface VMBalance { col: string; label: string; treated: number; control: number; display: string; frac: number; tone: string }
/** One piece of the narrative headline — `strong` renders bold. */
export interface VMNarrativeSegment { text: string; strong?: boolean }
/** The verdict-first plain-language conclusion painted atop the Answer tab. */
export interface VMNarrative { tone: string; segments: VMNarrativeSegment[] }
export interface VMAnswer {
    /** Display labels (the friendly `columns` names) — raw column names stay in the pickers. */
    treatment: string; outcome: string; unit: string;
    naive: number; naiveLo: number; naiveHi: number; effect: number; lo: number; hi: number;
    clear: boolean; flip: boolean; cautious: boolean;
    nTotal: bigint; nTreated: bigint; nControl: bigint; nCompared: bigint; nDropped: bigint;
    balance: VMBalance[];
}
/** A refusal zone (the engine returned `adjusted = none`). */
export interface VMRefusal {
    kind: 'positivity' | 'not_estimable';
    title: string; body: string;
    evidence: { label: string; value: string }[];
}
/** The propensity-overlap diagnostic (always derivable; the centrepiece of a
 *  positivity refusal, a small diagnostic card otherwise). */
export interface VMOverlap {
    treated: number[]; control: number[];
    commonSupportFrac: number; positivityOk: boolean; supportLabel: string;
}
export interface VMRefuteCheck {
    name: string;
    /** Two-or-three-word pill name for the Answer-tab trust strip. */
    short: string;
    desc: string; value: string; passed: boolean; tip: option<string>; help: HelpId;
}
export interface VMRefute { checks: VMRefuteCheck[]; sens: VMSensitivity | null }
export interface VMSensitivity {
    lo: number[]; mid: number[]; hi: number[]; xTicks: string[]; yTicks: string[];
    /** Observed-confounder benchmarks as chart marks (fractional index on the
     *  strengths axis) — "a hidden cause as strong as Incoming grade sits here". */
    marks: VMDoseMark[];
}
export interface VMDoseMark { at: number; label: string; tone: string; help: HelpId }
export interface VMMarginal { label: string; value: number; frac: number }
export interface VMDose {
    /** Display labels — the friendly `columns` names, falling back to the raw columns. */
    feature: string; outcome: string; lo: number[]; mid: number[]; hi: number[];
    xTicks: string[]; yTicks: string[]; marks: VMDoseMark[];
    recoLabel: string; recoEffect: number; recoLo: number; recoHi: number;
    tradeoff: string; marginal: VMMarginal[];
    /** One sentence placing this tab: the dose curve answers a DIFFERENT question
     *  than the yes/no headline (it is over a numeric feature, not the treatment). */
    framing: string;
}
export interface VMJournalRow {
    treatment: string; outcome: string; confounders: string; effect: string;
    verdict: string; verdictTone: string; who: string; when: string; preset: option<string>;
}

// ---------------------------------------------------------------------------
// Small numeric / formatting helpers.
// ---------------------------------------------------------------------------
const arr = (v: Vec | undefined): number[] => (v ? Array.from(v, Number) : []);
const arrI = (v: VecI | undefined): number[] => (v ? Array.from(v, Number) : []);
const fmt = (x: number): string => {
    // Normalise a `-0.0` (from `(-0.04).toFixed(1)`) to `0.0` — sign-confusing on
    // exactly the degenerate results where clarity matters most.
    const r = Number.isInteger(x) ? String(x) : x.toFixed(1);
    return r === '-0.0' ? '0.0' : r;
};
const signed = (x: number): string => {
    const f = fmt(x);
    return f === '0' || f === '0.0' || f.startsWith('-') ? f : `+${f}`;
};
const clamp01 = (x: number): number => (x < 0 ? 0 : x > 1 ? 1 : x);
const cap = (s: string): string => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);


/** Coarse imbalance band from a standardised mean difference. */
function band(absStd: number): { level: string; tone: string } {
    if (absStd >= 0.66) return { level: 'large gap', tone: 'neg' };
    if (absStd >= 0.33) return { level: 'some', tone: 'warn' };
    return { level: 'small', tone: 'muted' };
}

const colMeta = (meta: ColMeta | undefined, col: string) => meta?.get(col);
const unitOf = (meta: ColMeta | undefined, col: string): string => getSomeorUndefined(colMeta(meta, col)?.unit) ?? '';
/** The friendly display label for a column — the raw name when none is configured.
 *  Every word the surface SPEAKS uses this; raw column names stay only in the
 *  mono picker chips (where identity matters). */
const labelOf = (meta: ColMeta | undefined, col: string): string => getSomeorUndefined(colMeta(meta, col)?.label) ?? col;
const kindOf = (cols: Column[], name: string): Column['kind'] | undefined => cols.find(c => c.name === name)?.kind;

/** The domain noun for one row (singular + plural) — `record(s)` by default. */
export interface SubjectNouns { one: string; many: string }

// ---------------------------------------------------------------------------
// Verdict → tone + headline label.
// ---------------------------------------------------------------------------
const VERDICT_TONE: Record<VerdictTag, string> = {
    causal: 'pos',
    modest: 'warn',
    adjustment_insufficient: 'warn',
    non_identifiable_positivity: 'neg',
    not_estimable: 'muted',
};
const VERDICT_LABEL: Record<VerdictTag, string> = {
    causal: 'Causal effect',
    modest: 'Modest / unclear',
    adjustment_insufficient: 'Not trustworthy yet',
    non_identifiable_positivity: 'No fair comparison',
    not_estimable: 'Can’t be estimated',
};

function deriveVerdict(v: VerdictValue): VMVerdict {
    return { tag: v.type, tone: VERDICT_TONE[v.type], label: VERDICT_LABEL[v.type] };
}

// ---------------------------------------------------------------------------
// Config → set-up rail.
// ---------------------------------------------------------------------------
function treatmentKind(cols: Column[], treatment: string): string {
    switch (kindOf(cols, treatment)) {
        case 'boolean': return 'yes / no';
        case 'integer': return 'count';
        case 'float': return 'number';
        case 'string': return 'category';
        default: return 'value';
    }
}

function deriveSpec(config: ConfigValue, cols: Column[], result: ResultValue | null, meta: ColMeta | undefined, dataLen: number, subject: SubjectNouns): VMSpec {
    const outUnit = unitOf(meta, config.outcome);

    // Join the chosen confounders with their measured imbalance (if a result is
    // in yet). Reasons are templated from the sign of the gap.
    // Key balance by the BASE confounder the engine reports (categoricals one-hot
    // expand to many rows), keeping the most-imbalanced level so the rail reads the
    // worst gap.
    const balByCol = new Map<string, BalanceValue>();
    for (const b of result?.balance ?? []) {
        const prev = balByCol.get(b.base_column);
        if (!prev || Math.abs(b.std_diff) > Math.abs(prev.std_diff)) balByCol.set(b.base_column, b);
    }
    const confounders: VMConfounder[] = config.common_causes.map(col => {
        const b = balByCol.get(col);
        const absStd = b ? Math.abs(b.std_diff) : 0;
        const { level, tone } = band(absStd);
        const dir = b ? (b.treated_mean < b.control_mean ? 'lower' : 'higher') : '';
        const reason = b ? `the treated group ran ${dir} on ${labelOf(meta, col)}` : 'measured once you Run';
        return { col, reason, imbalance: clamp01(absStd), level, tone };
    });

    // Suggest the first column not already in play (treatment / outcome / confounder).
    const inPlay = new Set([config.treatment, config.outcome, ...config.common_causes]);
    const suggest = cols.find(c => !inPlay.has(c.name));
    const suggestion = suggest ? `add ${suggest.name}` : '';

    const methodV = getSomeorUndefined(config.method);
    const method = methodV?.type === 'propensity_score_weighting' ? 'reweighting' : 'regression';
    const targetV = getSomeorUndefined(config.estimand);
    const target = targetV && (targetV.type === 'att' || targetV.type === 'atc') ? 'treated' : 'all';

    return {
        treatment: config.treatment,
        treatmentLabel: labelOf(meta, config.treatment),
        treatmentKind: treatmentKind(cols, config.treatment),
        outcome: config.outcome,
        outcomeLabel: labelOf(meta, config.outcome),
        outcomeKind: outUnit ? `number · ${outUnit}` : 'number',
        comparison: kindOf(cols, config.treatment) === 'boolean' ? 'yes · vs no' : 'high · vs low',
        confounders, suggestion, method, target,
        // Always the CURRENT (filtered) dataset's row count — the result's own
        // n_total belongs to the Answer-tab counts strip; labelling the header
        // with it desyncs from the data after a population edit (and reads
        // wrong whenever the two legitimately differ).
        dataLabel: `${dataLen} ${subject.many}`,
    };
}

// ---------------------------------------------------------------------------
// Result → Answer tab (numeric — adjusted present).
// ---------------------------------------------------------------------------
function balanceDisplay(b: BalanceValue, categorical: Set<string>): string {
    // A one-hot level is a proportion (0..1) — format as %. Detect via the engine's
    // `base_column` so the level matches the original categorical confounder.
    const isProp = categorical.has(b.base_column) && b.treated_mean >= 0 && b.treated_mean <= 1 && b.control_mean >= 0 && b.control_mean <= 1;
    return isProp
        ? `${Math.round(b.treated_mean * 100)}% vs ${Math.round(b.control_mean * 100)}%`
        : `${b.treated_mean.toFixed(1)} vs ${b.control_mean.toFixed(1)}`;
}

function deriveAnswer(config: ConfigValue, result: ResultValue, adj: AdjustedValue, meta: ColMeta | undefined): VMAnswer {
    const ci = getSomeorUndefined(adj.ci);
    const nci = getSomeorUndefined(result.naive_ci);
    const categorical = new Set(getSomeorUndefined(config.categorical) ?? []);
    const balance: VMBalance[] = [...result.balance]
        .sort((a, b) => Math.abs(b.std_diff) - Math.abs(a.std_diff))
        .map(b => ({
            // One-hot rows are `col=level` — label the BASE confounder, keep the level.
            col: b.column,
            label: b.column === b.base_column ? labelOf(meta, b.base_column) : `${labelOf(meta, b.base_column)}${b.column.startsWith(b.base_column) ? b.column.slice(b.base_column.length) : ` (${b.column})`}`,
            treated: b.treated_mean, control: b.control_mean,
            display: balanceDisplay(b, categorical), frac: clamp01(Math.abs(b.std_diff)), tone: band(Math.abs(b.std_diff)).tone,
        }));
    const lo = ci?.lower ?? adj.effect;
    const hi = ci?.upper ?? adj.effect;
    const clear = result.verdict.type === 'causal' || (ci ? (lo > 0 || hi < 0) : false);
    const flip = Math.sign(result.naive) !== Math.sign(adj.effect) && result.naive !== 0;
    const nDropped = Number(result.n_dropped);
    const nTotal = Number(result.n_total);
    return {
        treatment: labelOf(meta, config.treatment), outcome: labelOf(meta, config.outcome), unit: unitOf(meta, config.outcome),
        naive: result.naive, naiveLo: nci?.lower ?? result.naive, naiveHi: nci?.upper ?? result.naive,
        effect: adj.effect, lo, hi, clear, flip,
        cautious: result.verdict.type === 'adjustment_insufficient',
        nTotal: result.n_total, nTreated: result.n_treated, nControl: result.n_control,
        nCompared: BigInt(Math.max(0, nTotal - nDropped)), nDropped: result.n_dropped,
        balance,
    };
}

// ---------------------------------------------------------------------------
// Narrative — the verdict-first conclusion sentence atop the Answer tab. Every
// word is templated from the verdict tag, the returned numbers, the friendly
// column labels and the subject noun — the same derived-never-authored rule,
// aimed at a spoken sentence instead of a badge.
// ---------------------------------------------------------------------------
function deriveNarrative(
    result: ResultValue, a: VMAnswer | null, refute: VMRefute | null,
    higherBetter: boolean | undefined, subject: SubjectNouns,
): VMNarrative | null {
    // Refusals lead with their own explanatory zone — no second headline.
    if (a === null) return null;
    const unit = a.unit ? ` ${a.unit}` : '';
    const dirUp = a.effect > 0;
    // With a good direction configured, speak in it ("improves"/"worsens");
    // otherwise stay neutral ("raises"/"lowers").
    const verb = higherBetter === undefined
        ? (dirUp ? 'raises' : 'lowers')
        : (dirUp === higherBetter ? 'improves' : 'worsens');
    const range = ` (likely between ${signed(a.lo)} and ${signed(a.hi)})`;
    // The stress-test clause — only when checks actually ran.
    const passed = refute?.checks.filter(c => c.passed).length ?? 0;
    const total = refute?.checks.length ?? 0;
    const trustClause = total === 0 ? ''
        : passed === total
            ? ` It held up against all ${total} ways we tried to break it.`
            : ` It passed ${passed} of ${total} stress tests — see “Can we trust it?”.`;

    switch (result.verdict.type) {
        case 'causal':
            return {
                tone: 'pos',
                segments: [
                    { text: 'Yes — ', strong: true },
                    { text: `${a.treatment} genuinely ${verb} ${a.outcome}: about ` },
                    { text: `${signed(a.effect)}${unit}`, strong: true },
                    { text: ` per ${subject.one}${range}.${trustClause}` },
                ],
            };
        case 'modest': {
            // Say WHICH gate made it modest — the engine's machine-readable
            // reasons, phrased with the echoed thresholds.
            const tags = new Set(result.verdict_reasons.map(v => v.type));
            const why = tags.has('not_material')
                ? ` — the change (${signed(a.effect)}${unit}) is smaller than what would matter here (±${fmt(result.materiality_threshold)}${unit}).`
                : tags.has('ci_spans_zero')
                    ? ` — the likely range (${signed(a.lo)} to ${signed(a.hi)}) includes zero.`
                    : tags.has('thin_support')
                        ? ' — too few treated and untreated look-alikes overlap to be confident.'
                        : tags.has('low_robustness')
                            ? ' — a fairly weak hidden factor could overturn it.'
                            : (a.lo <= 0 && a.hi >= 0 ? ` — the likely range (${signed(a.lo)} to ${signed(a.hi)}) includes zero.` : '.');
            return {
                tone: 'warn',
                segments: [
                    { text: 'Maybe — ', strong: true },
                    { text: `there may be an effect of ${a.treatment} on ${a.outcome}, but it’s too small or unclear to act on` },
                    { text: why },
                ],
            };
        }
        case 'adjustment_insufficient':
            return {
                tone: 'warn',
                segments: [
                    { text: 'Don’t act on this yet — ', strong: true },
                    { text: `we got a number (${signed(a.effect)}${unit}), but a stress test failed: something we didn’t adjust for may still be driving it.` },
                ],
            };
        default:
            return null;
    }
}

// ---------------------------------------------------------------------------
// Result → refusal zone (adjusted = none).
// ---------------------------------------------------------------------------
function deriveRefusal(config: ConfigValue, result: ResultValue, meta: ColMeta | undefined, subject: SubjectNouns): VMRefusal {
    const nT = Number(result.n_treated), nC = Number(result.n_control), nTot = Number(result.n_total);
    const treatment = labelOf(meta, config.treatment);
    if (result.verdict.type === 'not_estimable') {
        const reason = result.verdict.value || 'The treatment barely varies, so no comparison can be formed.';
        return {
            kind: 'not_estimable',
            title: `Can’t estimate the effect of ${treatment}`,
            body: reason,
            evidence: [
                { label: 'treated', value: String(nT) },
                { label: 'untreated', value: String(nC) },
                { label: subject.many, value: String(nTot) },
            ],
        };
    }
    if (result.verdict.type === 'non_identifiable_positivity') {
        const pct = Math.round(result.overlap.common_support_frac * 100);
        return {
            kind: 'positivity',
            title: 'No like-for-like comparison exists',
            body: `The treated and untreated groups barely overlap on the confounders — only ${pct}% sit in a range where both occur — so there is no fair comparison to adjust toward.`,
            evidence: [
                { label: 'common support', value: `${pct}%` },
                { label: 'treated', value: String(nT) },
                { label: 'untreated', value: String(nC) },
            ],
        };
    }
    // Defensive: adjusted=none under any other verdict (contract drift) — refuse
    // plainly rather than fabricate a positivity explanation.
    return {
        kind: 'not_estimable',
        title: `Can’t estimate the effect of ${treatment}`,
        body: 'The engine could not produce a like-for-like estimate for this configuration.',
        evidence: [
            { label: 'treated', value: String(nT) },
            { label: 'untreated', value: String(nC) },
            { label: subject.many, value: String(nTot) },
        ],
    };
}

// ---------------------------------------------------------------------------
// Result → overlap diagnostic (the propensity histogram).
// ---------------------------------------------------------------------------
function deriveOverlap(o: OverlapValue): VMOverlap {
    const pct = Math.round(o.common_support_frac * 100);
    return {
        treated: arr(o.treated_propensity),
        control: arr(o.control_propensity),
        commonSupportFrac: o.common_support_frac,
        positivityOk: o.positivity_ok,
        supportLabel: `${pct}% common support`,
    };
}

// ---------------------------------------------------------------------------
// Refutation → Trust tab (flat scalar fields; only the requested checks).
// ---------------------------------------------------------------------------
/** First x where the piecewise-linear (x,y) curve crosses zero, or null. */
function zeroCrossing(xs: number[], ys: number[]): number | null {
    for (let i = 1; i < ys.length && i < xs.length; i++) {
        const y0 = ys[i - 1]!, y1 = ys[i]!;
        if ((y0 > 0 && y1 <= 0) || (y0 < 0 && y1 >= 0)) {
            const t = y0 / (y0 - y1);
            return xs[i - 1]! + t * (xs[i]! - xs[i - 1]!);
        }
    }
    return null;
}

function deriveRefute(r: RefutationValue, adj: AdjustedValue | undefined, meta: ColMeta | undefined): VMRefute {
    const checks: VMRefuteCheck[] = [];
    const est = adj?.effect ?? 0;

    const placeboEffect = getSomeorUndefined(r.placebo_effect);
    const placeboPasses = getSomeorUndefined(r.placebo_passes);
    if (placeboEffect !== undefined || placeboPasses !== undefined) {
        const passed = placeboPasses ?? (placeboEffect !== undefined && Math.abs(placeboEffect) < Math.max(0.5, Math.abs(est) * 0.15));
        checks.push({
            name: 'Shuffle test',
            short: 'shuffle',
            desc: 'Shuffle which rows were treated; a real effect should collapse to zero.',
            // Speak the outcome, not the statistic: on fake labels the effect
            // should VANISH — say whether it did.
            value: placeboEffect !== undefined
                ? (passed ? `vanished (${signed(placeboEffect)})` : `didn’t vanish (${signed(placeboEffect)})`)
                : (passed ? 'vanished' : 'didn’t vanish'),
            passed,
            tip: some('Randomly re-label which rows were treated. A genuine effect should vanish.'),
            help: 'check_shuffle',
        });
    }
    const ds = getSomeorUndefined(r.data_subset_effect);
    if (ds !== undefined) {
        const dstd = getSomeorUndefined(r.data_subset_std) ?? 0;
        const passed = Math.abs(ds - est) < Math.max(0.5, Math.abs(est) * 0.2);
        checks.push({
            name: 'Drop-some test',
            short: 'drop-some',
            desc: 'Re-estimate on random subsamples; a trustworthy effect stays put.',
            value: passed ? `stayed at ${signed(ds)} ± ${fmt(dstd)}` : `moved to ${signed(ds)} ± ${fmt(dstd)}`,
            passed,
            tip: none,
            help: 'check_dropsome',
        });
    }
    const rcc = getSomeorUndefined(r.random_cc_within_ci);
    if (rcc !== undefined) {
        checks.push({
            name: 'Decoy cause',
            short: 'decoy',
            desc: 'Add an irrelevant random factor; the answer should not move.',
            value: rcc ? 'didn’t move' : 'moved',
            passed: rcc,
            tip: none,
            help: 'check_decoy',
        });
    }
    const sens = getSomeorUndefined(r.sensitivity);
    const evalue = getSomeorUndefined(r.robustness_value);
    // Benchmarks put the abstract strengths axis in the user's own vocabulary —
    // "as strong as Incoming grade" — sorted strongest-first.
    const benchmarks = sens ? [...sens.benchmarks].sort((a, b) => b.strength - a.strength) : [];
    if (sens !== undefined || evalue !== undefined) {
        const strengths = sens ? arr(sens.strengths) : [];
        const effects = sens ? arr(sens.effects) : [];
        const tip = sens ? zeroCrossing(strengths, effects) : null;
        const strongest = benchmarks[0];
        // Phrase the tipping point AGAINST the strongest known factor when we can:
        // beyond it → reassuring; short of it → concrete and alarming.
        const value = tip !== null
            ? (strongest !== undefined
                ? (tip > strongest.strength
                    ? `flips only beyond ${labelOf(meta, strongest.column)}`
                    : `a cause weaker than ${labelOf(meta, strongest.column)} could flip it`)
                : `would flip at ${fmt(tip)}`)
            : evalue !== undefined ? `holds (E-value ${fmt(evalue)})` : 'holds throughout';
        const scaleNote = strongest !== undefined
            ? ` For scale, your strongest known factor (${labelOf(meta, strongest.column)}) sits at ${fmt(strongest.strength)} on this axis.`
            : '';
        checks.push({
            name: 'Hidden cause',
            short: 'hidden cause',
            desc: `How strong an unrecorded common cause would need to be to overturn the result.${scaleNote}`,
            value,
            passed: tip === null,
            tip: some('Something unrecorded could drive both the treatment choice and the outcome.'),
            help: 'check_hidden',
        });
    }

    // Sensitivity band: the unobserved curve is the mid line; the band carries
    // the estimate's CI half-width (≈ constant, as in practice).
    let sensVM: VMSensitivity | null = null;
    if (sens !== undefined) {
        const mid = arr(sens.effects);
        if (mid.length) {
            const ci = getSomeorUndefined(adj?.ci);
            const half = ci ? Math.abs(ci.upper - ci.lower) / 2 : 0;
            const lo = mid.map(m => m - half);
            const hi = mid.map(m => m + half);
            const yLo = Math.min(0, ...lo), yHi = Math.max(...hi, 0);
            // Up to two strongest observed-confounder benchmarks as chart marks —
            // strength → fractional index on the strengths axis.
            const strengths = arr(sens.strengths);
            const atOf = (s: number): number | null => {
                if (strengths.length < 2) return null;
                if (s <= strengths[0]!) return 0;
                for (let i = 1; i < strengths.length; i++) {
                    if (s <= strengths[i]!) {
                        const span = strengths[i]! - strengths[i - 1]!;
                        return span > 0 ? i - 1 + (s - strengths[i - 1]!) / span : i;
                    }
                }
                return strengths.length - 1;
            };
            const marks: VMDoseMark[] = benchmarks.slice(0, 2).flatMap(b => {
                const at = atOf(b.strength);
                return at === null ? [] : [{ at, label: `as strong as ${labelOf(meta, b.column)}`, tone: 'muted', help: 'sensitivity' as HelpId }];
            });
            sensVM = { lo, mid, hi, xTicks: ['none', '', 'stronger'], yTicks: [fmt(yLo), fmt((yLo + yHi) / 2), fmt(yHi)], marks };
        }
    }
    return { checks, sens: sensVM };
}

// ---------------------------------------------------------------------------
// Dose response → How-much tab.
// ---------------------------------------------------------------------------
function deriveDose(dose: DoseValue, config: ConfigValue, meta: ColMeta | undefined): VMDose {
    const grid = arr(dose.grid);
    const mid = arr(dose.effect);
    const lo = getSomeorUndefined(dose.lower) ? arr(getSomeorUndefined(dose.lower)) : mid.slice();
    const hi = getSomeorUndefined(dose.upper) ? arr(getSomeorUndefined(dose.upper)) : mid.slice();
    const sizes = arrI(dose.size);
    // The recommendation is a point on the FEATURE x-axis (e.g. days), never the
    // outcome unit — labelling "3" as "3 MPa" would be semantically wrong.
    const featureUnit = unitOf(meta, dose.feature);

    // "you are here" = the busiest dose bucket; "sweet spot" = where the lower CI
    // first clears zero and marginal gain has flattened.
    const here = sizes.length ? sizes.indexOf(Math.max(...sizes)) : 0;
    const marg = mid.map((m, i) => (i === 0 ? 0 : m - mid[i - 1]!));
    const maxMarg = Math.max(1e-9, ...marg.map(Math.abs));
    let sweet = mid.length - 1;
    for (let i = 1; i < mid.length; i++) {
        if (lo[i]! > 0 && Math.abs(marg[i]!) < 0.25 * maxMarg) { sweet = i; break; }
    }

    const xi = grid.length ? [0, Math.floor(grid.length / 2), grid.length - 1] : [];
    const xTicks = xi.map(i => fmt(grid[i]!));
    const yLo = Math.min(0, ...lo), yHi = Math.max(...hi, 0);
    const yTicks = [fmt(yLo), fmt((yLo + yHi) / 2), fmt(yHi)];

    // Trim the trailing flat tail (steps whose marginal gain is negligible) so
    // the strip shows only the decision-relevant range.
    const margSteps = marg.slice(1);
    let lastStep = margSteps.length - 1;
    while (lastStep > 0 && Math.abs(margSteps[lastStep]!) < 0.12 * maxMarg) lastStep--;
    const marginal: VMMarginal[] = margSteps.slice(0, lastStep + 1).map((v, i) => ({ label: `${fmt(grid[i + 1]!)}`, value: v, frac: clamp01(Math.abs(v) / maxMarg) }));

    const stepFrom = here < sweet ? here : Math.max(0, sweet - 1);
    const gainToSweet = (mid[sweet] ?? 0) - (mid[stepFrom] ?? 0);
    const nextGain = sweet + 1 < mid.length ? mid[sweet + 1]! - mid[sweet]! : 0;
    const tradeoff = `Going from ${fmt(grid[stepFrom] ?? 0)} to ${fmt(grid[sweet] ?? 0)} adds ${signed(gainToSweet)}; the next step adds only ${signed(nextGain)}.`;

    const marks: VMDoseMark[] = [];
    if (sizes.length) marks.push({ at: here, label: 'you are here', tone: 'muted', help: 'dose_here' });
    marks.push({ at: sweet, label: 'sweet spot', tone: 'pos', help: 'dose_sweet' });

    const featureLabel = labelOf(meta, dose.feature);
    const outcomeLabel = labelOf(meta, config.outcome);
    return {
        feature: featureLabel, outcome: outcomeLabel, lo, mid, hi, xTicks, yTicks, marks,
        // Name the feature in the recommendation — a bare "≈ 5.5" doesn't say
        // 5.5 of WHAT.
        recoLabel: `aim for ${featureLabel} ≈ ${fmt(grid[sweet] ?? 0)}${featureUnit ? ' ' + featureUnit : ''}`,
        recoEffect: mid[sweet] ?? 0, recoLo: lo[sweet] ?? 0, recoHi: hi[sweet] ?? 0,
        tradeoff, marginal,
        framing: `A different question from the headline: how ${outcomeLabel} responds as ${featureLabel} itself moves.`,
    };
}

// ---------------------------------------------------------------------------
// Journal.
// ---------------------------------------------------------------------------
function relTime(d: Date, now: Date): string {
    const days = Math.floor((now.getTime() - d.getTime()) / 86_400_000);
    if (days <= 0) return 'today';
    if (days === 1) return 'yesterday';
    if (days < 7) return d.toLocaleDateString(undefined, { weekday: 'short' });
    return 'last wk';
}

/** Short journal word + tone from the STORED verdict (not recomputed). */
const VERDICT_WORD: Record<VerdictTag, string> = {
    causal: 'causal',
    modest: 'modest',
    adjustment_insufficient: 'not robust',
    non_identifiable_positivity: 'no overlap',
    not_estimable: 'n/a',
};

function deriveJournalRow(row: JournalRowValue, now: Date, meta: ColMeta | undefined): VMJournalRow {
    const adj = getSomeorUndefined(row.adjusted);
    return {
        treatment: labelOf(meta, row.config.treatment), outcome: labelOf(meta, row.config.outcome),
        confounders: row.config.common_causes.map(c => labelOf(meta, c)).join(', '),
        effect: adj !== undefined ? signed(adj) : signed(row.naive),
        verdict: VERDICT_WORD[row.verdict.type],
        verdictTone: VERDICT_TONE[row.verdict.type],
        who: row.committed_by, when: relTime(row.committed_at, now),
        // The originating preset id (resolved to a label by the surface); `none` for a
        // free-form / pre-presets row.
        preset: row.preset,
    };
}

// ---------------------------------------------------------------------------
// Public entry — assemble the whole view-model.
// ---------------------------------------------------------------------------
export interface ExperimentView {
    spec: VMSpec;
    verdict: VMVerdict | null;
    /** The verdict-first conclusion sentence (numeric verdicts only — refusals
     *  lead with their own explanatory zone). */
    narrative: VMNarrative | null;
    answer: VMAnswer | null;
    refusal: VMRefusal | null;
    overlap: VMOverlap | null;
    refute: VMRefute | null;
    dose: VMDose | null;
    journal: VMJournalRow[] | null;
}

/**
 * Derive the full view-model from the staged config, the bound data's columns
 * and the (possibly absent) single experiment result.
 *
 * @param config - The staged {@link ConfigValue}.
 * @param ranConfig - The config that produced `result` (the result deck reads this so its
 *   strings never drift ahead of the numbers on a live edit before the next Run).
 * @param cols - The bound dataset's columns (name + type family).
 * @param result - The experiment result, or `null` until the first Run settles.
 * @param journal - Committed rows, or `null`.
 * @param meta - Optional per-column display metadata.
 * @param dataLen - Row count of the bound dataset (for the header label).
 * @param now - "Now" for relative journal timestamps (injected for determinism).
 * @param subject - The domain noun for one row (defaults to `record(s)`).
 */
export function deriveView(
    config: ConfigValue,
    ranConfig: ConfigValue,
    cols: Column[],
    result: ResultValue | null,
    journal: JournalRowValue[] | null,
    meta: ColMeta | undefined,
    dataLen: number,
    now: Date,
    subject: SubjectNouns = { one: 'record', many: 'records' },
): ExperimentView {
    const adj = result ? getSomeorUndefined(result.adjusted) : undefined;
    const refutation = result ? getSomeorUndefined(result.refutation) : undefined;
    const doseR = result ? getSomeorUndefined(result.dose_response) : undefined;
    const answer = result && adj ? deriveAnswer(ranConfig, result, adj, meta) : null;
    const refute = refutation ? deriveRefute(refutation, adj, meta) : null;
    const higherBetter = getSomeorUndefined(colMeta(meta, ranConfig.outcome)?.higherIsBetter);
    return {
        // The set-up rail reflects the LIVE config (the editor); the result deck
        // reflects RANCONFIG — the config that produced `result` — so the result
        // strings never drift ahead of the numbers on a live picker edit.
        spec: deriveSpec(config, cols, result, meta, dataLen, subject),
        verdict: result ? deriveVerdict(result.verdict) : null,
        narrative: result ? deriveNarrative(result, answer, refute, higherBetter, subject) : null,
        answer,
        refusal: result && !adj ? deriveRefusal(ranConfig, result, meta, subject) : null,
        overlap: result ? deriveOverlap(result.overlap) : null,
        refute,
        dose: doseR && doseR.effect.length ? deriveDose(doseR, ranConfig, meta) : null,
        journal: journal ? journal.map(r => deriveJournalRow(r, now, meta)) : null,
    };
}

// ---------------------------------------------------------------------------
// Validation design — the "Validate" tab view-model.
// ---------------------------------------------------------------------------

/** Decoded `ExperimentDesignType` (the `design` function's result). */
export type DesignValue = ValueTypeOf<typeof Experiment.Types.Design>;

export interface VMDesignOption { label: string; nTotal: number; nTreated: number; nControl: number; treatedShare: number }
export interface VMDesignMatch { col: string; frac: number; tone: string; display: string }
export interface VMDesign {
    /** Verdict-framed eyebrow headline. */
    headline: string;
    rationale: string;
    /** The recommended (first) option — the KPI + split meter read from this. */
    primary: VMDesignOption;
    /** Alternate split options (rows under the primary). */
    alternates: VMDesignOption[];
    /** Categories to match the arms on, with imbalance bar widths from the result. */
    matchOn: VMDesignMatch[];
    /** Power curve: total-N → chance of detecting (0..100), with target + "you're here". */
    curve: { mid: number[]; xTicks: string[]; marks: VMDoseMark[] };
    /** Signed target effect with unit, e.g. "+5.2 MPa". */
    targetLabel: string;
    targetPctLabel: string;
    /** create_control → frame the split as a hold-back; no current-power marker. */
    holdback: boolean;
    /** non_identifiable_positivity → point at the overlap chart. */
    showOverlap: boolean;
    /** Effect too faint to size sanely — the head-count is a capped sentinel. */
    faint: boolean;
}

// Basis → eyebrow headline (the verdict-driven framing).
const DESIGN_HEADLINE: Record<string, string> = {
    detect_observed: 'Confirm it with a controlled trial',
    de_bias: 'Make it trustworthy with a controlled trial',
    resolve_vs_null: 'Settle it with a bigger trial',
    restrict_to_overlap: 'You can’t compare yet',
    create_control: 'Hold some back next time',
};

function deriveDesignOption(o: DesignValue['options'][number]): VMDesignOption {
    return {
        label: o.label,
        nTotal: Number(o.n_total),
        nTreated: Number(o.n_treated),
        nControl: Number(o.n_control),
        treatedShare: o.treated_share,
    };
}

/** Design value → the "Validate" tab view-model (numbers → recipe + chart). */
export function deriveDesign(d: DesignValue, result: ResultValue | null, ranConfig: ConfigValue, meta: ColMeta | undefined): VMDesign {
    const basis = d.basis.type;
    const outcome = ranConfig.outcome;
    const unit = unitOf(meta, outcome);
    const options = d.options.map(deriveDesignOption);
    const primary = options[0] ?? { label: 'Even split', nTotal: 0, nTreated: 0, nControl: 0, treatedShare: 0.5 };

    // Match-on bars: join each category to the result's pre-adjustment imbalance,
    // by BASE confounder (categoricals one-hot expand to col=level / col_level).
    const balByCol = new Map<string, BalanceValue>();
    for (const b of result?.balance ?? []) {
        const prev = balByCol.get(b.base_column);
        if (!prev || Math.abs(b.std_diff) > Math.abs(prev.std_diff)) balByCol.set(b.base_column, b);
    }
    const matchOn: VMDesignMatch[] = d.match_on.map(col => {
        const b = balByCol.get(col);
        const absStd = b ? Math.abs(b.std_diff) : 0;
        const { tone } = band(absStd);
        return { col, frac: clamp01(absStd), tone, display: getSomeorUndefined(colMeta(meta, col)?.label) ?? col };
    });

    // Power curve → a single mid line (lo=hi=mid → no band); a "target" mark at the
    // bin nearest the recommended N, and "you're here" at the current sample size.
    // Zip n/power to the common length — the contract allows divergence; a defined
    // current power past the grid is shown off-scale rather than dropped silently.
    const ns = arrI(d.power_curve.n);
    const powers = arr(d.power_curve.power);
    const len = Math.min(ns.length, powers.length);
    const nearest = (target: number) => {
        let bi = 0, bd = Infinity;
        for (let i = 0; i < len; i++) { const dd = Math.abs(ns[i]! - target); if (dd < bd) { bd = dd; bi = i; } }
        return bi;
    };
    const xTicks = len ? [String(ns[0]), String(ns[Math.floor((len - 1) / 2)]), String(ns[len - 1])] : [];
    const marks: VMDoseMark[] = [];
    if (len) marks.push({ at: nearest(primary.nTotal), label: `target ${(d.target_power * 100).toFixed(0)}%`, tone: 'pos', help: 'validate_power' });
    // "you're here" — only when there's a comparison group to power from.
    const cp = getSomeorUndefined(d.current_power);
    if (cp !== undefined && len) {
        const curN = result ? Number(result.n_total) : 0;
        if (curN > 0) {
            const offScale = curN >= (ns[len - 1] ?? 0);
            marks.push({
                at: offScale ? len - 1 : nearest(curN),
                label: offScale ? `you’re here · ${(cp * 100).toFixed(0)}% (off-scale)` : `you’re here ${(cp * 100).toFixed(0)}%`,
                tone: 'muted', help: 'validate_power',
            });
        }
    }

    // Sentinel: the engine clamps the standardised effect to ≥0.01, so an effect
    // too faint to size sanely returns a capped head-count — flag it, don't present
    // a precise-looking million as a recommendation.
    const faint = d.outcome_sd > 0 && Math.abs(d.target_effect) / d.outcome_sd <= 0.011;

    return {
        headline: DESIGN_HEADLINE[basis] ?? 'Validate this',
        rationale: d.rationale,
        primary,
        alternates: options.slice(1),
        matchOn,
        curve: { mid: powers.slice(0, len).map(p => p * 100), xTicks, marks },
        targetLabel: `${signed(d.target_effect)}${unit ? ` ${unit}` : ''}`,
        targetPctLabel: `${(d.target_power * 100).toFixed(0)}%`,
        holdback: basis === 'create_control',
        showOverlap: basis === 'restrict_to_overlap',
        faint,
    };
}

// Derived helpers the shell also needs for the header / badge / banner.
export { fmt, signed, cap, unitOf };
